<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import GbBreadcrumbs, { type BreadcrumbItem } from '@/components/GbBreadcrumbs.vue'
import { getAreasOfNeed } from '@/presentlevels/api_v1.js'
import { useVisibilityStore } from '@/visibility/visibility.store'

type AreaOfNeed = { label: string; value: string }

const STUDENT_INFO_ROUTE = 'PresentLevelsExistingWizard'
const SUBJECT_RESULTS_ROUTE = 'PresentlevelsSubjectResults'
const CONCLUSION_ROUTE = 'PresentlevelsConclusion'
const CONCLUSION_ROUTES = [CONCLUSION_ROUTE, 'PresentlevelsConclusionAssessments', 'PresentlevelsConclusionGoalsStrategies']

const props = defineProps<{
  uuid: string
  availableStages: string[]
}>()

const route = useRoute()
const { selVisibility } = storeToRefs(useVisibilityStore())

const stagesInWizardOrder = computed<AreaOfNeed[]>(() =>
  getAreasOfNeed({ selBlocked: !selVisibility.value, iodSubject: false }).filter((area: AreaOfNeed) =>
    props.availableStages.includes(area.value)
  )
)

function isCurrentSubject(subject: string) {
  return route.params.subject === subject
}

function isOnRoute(routeName: string) {
  return route.name === routeName
}

function isOnConclusion() {
  return CONCLUSION_ROUTES.includes(String(route.name))
}

function subjectCrumb(area: AreaOfNeed): BreadcrumbItem {
  return {
    label: area.label,
    to: { name: SUBJECT_RESULTS_ROUTE, params: { uuid: props.uuid, subject: area.value } },
    isCurrent: isCurrentSubject(area.value)
  }
}

const crumbs = computed<BreadcrumbItem[]>(() => [
  { label: 'Student Info', to: { name: STUDENT_INFO_ROUTE, params: { uuid: props.uuid } }, isCurrent: isOnRoute(STUDENT_INFO_ROUTE) },
  ...stagesInWizardOrder.value.map(subjectCrumb),
  { label: 'Drafted Present Levels', to: { name: CONCLUSION_ROUTE, params: { uuid: props.uuid } }, isCurrent: isOnConclusion() }
])
</script>

<template>
  <GbBreadcrumbs :items="crumbs" aria-label="Breadcrumb" class="mb-5" />
</template>
